// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Outsize — three modes that PLAY differently (principle #14), not three dials. The
// spread is the STAT being compared, which genuinely changes which country is bigger
// and how well a human can call it:
//   Populace — population. The one most people have some feel for.
//   Expanse  — land area. Often counter-intuitive against population (a crowded
//              small country vs an empty huge one), so it reads as a different game.
//   Motley   — the stat rotates each round (population / area / density), announced,
//              so you cannot autopilot on one mental model.
//
// The other lever is the RAMP: the required ratio between the two countries tightens
// as the streak grows (a blowout early, a squeaker late), so early luck cannot carry
// a run and every streak eventually meets a pair it cannot call.

import type { StatId } from './data';

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** The stat(s) this mode compares; Motley rotates through them by pair index. */
  stats: StatId[];
  /** Per-pair answer budget in versus (ms). Solo is untimed. */
  pairMs: number;
}

// The ramp: pair `idx` requires the bigger value to exceed the smaller by a ratio in
// [rMin, rMin*BAND]. rMin decays from START toward the stat's floor.
export const RAMP_START = 2.4; // first pair is a ~2.4× blowout
export const RAMP_K = 6; // e-folding length in pairs
export const RAMP_BAND = 1.3; // width of the ratio window above rMin (tight, so late pairs stay hard)
export const MAX_PAIRS = 80; // sequence length backstop (survive all = a "maxed" win)

export const MODES: Record<string, Mode> = {
  populace: {
    id: 'populace',
    name: 'Populace',
    blurb: 'Which country has more PEOPLE? The stat most of us have a feel for — but the gap tightens every round.',
    stats: ['pop'],
    pairMs: 12_000,
  },
  expanse: {
    id: 'expanse',
    name: 'Expanse',
    blurb: 'Which country covers more GROUND? Area plays against your population instinct — the crowded ones are often small.',
    stats: ['area'],
    pairMs: 12_000,
  },
  motley: {
    id: 'motley',
    name: 'Motley',
    blurb: 'The question changes every round — people, area, or how CROWDED. No autopilot; read what it asks.',
    stats: ['pop', 'area', 'density'],
    pairMs: 13_000,
  },
};

export const MODE_IDS = ['populace', 'expanse', 'motley'] as const;
export const DEFAULT_MODE_ID = 'populace';

/** Validate an id off the wire and fall back rather than hand `undefined` on. */
export function modeOf(id: string | null | undefined): Mode {
  if (id && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE_ID];
}

/** The [rMin, rMax] ratio window for the pair at `idx`, floored per stat. */
export function ratioBand(idx: number, floor: number): [number, number] {
  const rMin = floor + (RAMP_START - floor) * Math.exp(-idx / RAMP_K);
  return [rMin, rMin * RAMP_BAND];
}

/** The stat a mode asks about on pair `idx` (Motley rotates; others are fixed). */
export function statForPair(mode: Mode, idx: number): StatId {
  return mode.stats[idx % mode.stats.length];
}
